import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ContactForm from './ContactForm';
import { motion } from '../motion/api';

const ContactModalContext = createContext({ open: () => {} });

export const useContactModal = () => useContext(ContactModalContext);

// Opens the contact form in a dialog from anywhere: const { open } = useContactModal(); open(event.currentTarget)
export function ContactModalProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const returnFocus = useRef(null);
  const dialogRef = useRef(null);
  const location = useLocation();

  const open = useCallback((trigger) => {
    returnFocus.current = trigger || document.activeElement;
    setIsOpen(true);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  // Close on route change.
  useEffect(() => { setIsOpen(false); }, [location.pathname]);

  // Scroll lock, inert background, focus trap, Esc (same pattern as the mobile menu).
  useEffect(() => {
    if (!isOpen) return undefined;
    const bg = Array.from(document.querySelectorAll('#root > .App > *:not(.cmodal)'));
    bg.forEach((el) => el.setAttribute('inert', ''));
    motion.stop();

    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); setIsOpen(false); return; }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const f = dialogRef.current.querySelectorAll('button:not([disabled]), [href], input:not([tabindex="-1"]), select, textarea, [tabindex]:not([tabindex="-1"])');
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    const trigger = returnFocus.current;
    return () => {
      document.removeEventListener('keydown', onKey);
      bg.forEach((el) => el.removeAttribute('inert'));
      motion.start();
      if (trigger && trigger.focus) trigger.focus({ preventScroll: true });
    };
  }, [isOpen]);

  return (
    <ContactModalContext.Provider value={{ open, close }}>
      {children}
      {isOpen && (
        <div className="cmodal" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
          <div className="cmodal__dialog" role="dialog" aria-modal="true" aria-labelledby="cmodal-title" ref={dialogRef} data-lenis-prevent>
            <button type="button" className="cmodal__close" onClick={close} aria-label="Close">
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>
            <div className="cmodal__head">
              <p className="eyebrow">Contact Moneli</p>
              <h2 className="cmodal__title" id="cmodal-title">Tell us what you'd like to <em>automate</em></h2>
            </div>
            <ContactForm idPrefix="cm" autoFocus />
          </div>
        </div>
      )}
    </ContactModalContext.Provider>
  );
}
